import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, ApiError, uploadFile } from './api';
import type { DentalAnnotations, DentalAsset, DentalRevision, DentalTask } from './types';

export const useDentalTaskStore = defineStore('dental-task', () => {
  const task = ref<DentalTask>(); const history = ref<DentalRevision[]>([]);
  const viewedRevisionId = ref('');
  const error = ref(''); const busy = ref(false); const editable = ref(false); const savedMessage = ref('');
  const sessionId = crypto.randomUUID();
  let leaseToken = ''; let timer: ReturnType<typeof setInterval> | undefined;
  const sourceVolume = computed(() => task.value?.assets.find(a => a.id === task.value?.volumeAssetId));
  const sourceMesh = computed(() => task.value?.assets.find(a => a.id === task.value?.meshAssetId));
  function leaseBody() { return { sessionId, leaseToken }; }
  function stopLease() { if (timer) clearInterval(timer); timer = undefined; editable.value = false; leaseToken = ''; }
  async function heartbeat() {
    if (!task.value || !editable.value) return;
    try { await api(`dental/tasks/${task.value.id}/heartbeat`, 'POST', leaseBody()); }
    catch (e) { editable.value = false; error.value = (e as Error).message; }
  }
  async function load(id: string) {
    stopLease();
    busy.value = true; error.value = ''; savedMessage.value = ''; editable.value = false;
    try {
      task.value = await api(`dental/tasks/${id}`);
      viewedRevisionId.value = task.value!.activeRevisionId;
      history.value = await api(`dental/tasks/${id}/revisions`);
      if (!task.value?.capabilities?.edit) return;
      try {
        const checkout = await api<{ lease: { token: string }; taskVersion: number; baseRevisionId: string }>(`dental/tasks/${id}/checkout`, 'POST', { sessionId });
        leaseToken = checkout.lease.token;
        // Re-read after checkout: another editor could have saved during load.
        task.value = await api(`dental/tasks/${id}`);
        viewedRevisionId.value = task.value!.activeRevisionId;
        editable.value = true;
        if (timer) clearInterval(timer);
        timer = setInterval(heartbeat, 30000);
      } catch (e) { if (e instanceof ApiError && [403, 409].includes(e.status)) error.value = e.message; else throw e; }
    } finally { busy.value = false; }
  }
  async function workflow(action: 'assign' | 'submit' | 'review' | 'reopen', extra: Record<string, unknown> = {}) {
    if (!task.value) throw new Error('Load a task first.');
    const snapshot = task.value;
    try {
      const result = await api<DentalTask>(`dental/tasks/${snapshot.id}/${action}`, 'POST', {
        version: snapshot.version, baseRevisionId: snapshot.activeRevisionId, workflowVersion: snapshot.workflowVersion,
        ...(action === 'submit' ? leaseBody() : {}), ...extra,
      });
      stopLease(); task.value = { ...snapshot, ...result };
      task.value = await api(`dental/tasks/${snapshot.id}`);
      error.value = '';
    } catch (e) { if (e instanceof ApiError && e.status === 409) { stopLease(); error.value = e.message; } throw e; }
  }
  async function annotationAsset(id: string) { return api<DentalAsset>(`dental/assets/${id}`); }
  async function stageFile(file: File, kind: 'labelmap' | 'meshLabels', extra: Record<string, unknown>) {
    if (!task.value || !editable.value) throw new Error('Reopen the task to acquire an editor lease.');
    const { assetFolderId } = await api<{ assetFolderId: string }>(`dental/cases/${task.value.caseId}`);
    const fileId = await uploadFile(assetFolderId, file);
    return api<DentalAsset>(`dental/cases/${task.value.caseId}/assets`, 'POST', {
      kind, fileId, clientRequestId: crypto.randomUUID(), ...extra,
      provenance: { dataKind: 'browser-annotation', sourceTaskId: task.value.id, sourceRevisionId: task.value.activeRevisionId },
    });
  }
  async function save(annotations: DentalAnnotations) {
    if (!task.value || !editable.value) throw new Error('Reopen the task to acquire an editor lease.');
    const payload = { ...leaseBody(), version: task.value.version, baseRevisionId: task.value.activeRevisionId,
      clientRequestId: crypto.randomUUID(), annotations };
    try {
      const saved = await api<{ task: DentalTask; revision: DentalRevision }>(`dental/tasks/${task.value.id}/revisions`, 'POST', payload);
      task.value = { ...task.value, ...saved.task, revision: saved.revision };
      viewedRevisionId.value = saved.revision.id;
      savedMessage.value = `Saved version ${saved.revision.number}`;
      try { history.value = await api(`dental/tasks/${task.value.id}/revisions`); }
      catch { error.value = 'Annotation saved. History could not be refreshed; reload to retry.'; }
      return saved.revision;
    } catch (e) { if (e instanceof ApiError && e.status === 409) editable.value = false; throw e; }
  }
  function dispose() {
    if (timer) clearInterval(timer);
    if (task.value && editable.value) void api(`dental/tasks/${task.value.id}/release`, 'POST', leaseBody()).catch(() => {});
    stopLease();
  }
  return { task, history, viewedRevisionId, error, busy, editable, savedMessage, sourceVolume, sourceMesh, load, annotationAsset, stageFile, save, workflow, dispose };
});
